import { count, eq, sql } from "drizzle-orm";
import type {
  CertificationsInsert,
  CoachDetailsInsert,
  CoachingResultsInsert,
  CoachInsert,
  OfferBenefitInsert,
  OfferComponentInsert,
  OfferInsert,
  OfferTagInsert,
  ProfileInsert,
  ProfileSelect,
  TestimonialInsert,
  ResourceInsert,
  OfferBonusInsert,
  OfferFaqInsert,
  CoachDetailsSelect,
} from "../../../drizzle/schema";
import {
  certifications,
  coachDetails,
  coaches,
  coachingResults,
  offerBenefits,
  offerComponents,
  offers,
  offerTags,
  profiles,
  testimonials,
  resources,
  offerBonuses,
  offerFaqs,
} from "../../../drizzle/schema";
import { getDBWithInit } from "../db";

export const createCoach = async (newCoach: CoachInsert) => {
  return (
    await (await getDBWithInit()).insert(coaches).values(newCoach).returning()
  )[0];
};

export const updateCoach = async (
  id: string,
  updatedCoach: Partial<CoachInsert>,
) => {
  return (
    await (await getDBWithInit())
      .update(coaches)
      .set(updatedCoach)
      .where(eq(coaches.id, id))
      .returning()
  )[0];
};

export const getCoach = async (id: string) => {
  return (await getDBWithInit()).query.coaches.findFirst({
    where: (coaches, { eq }) => eq(coaches.id, id),
    with: {
      profile: true,
      mainOffer: {
        with: {
          benefits: {
            orderBy: (offerBenefits, { asc }) => asc(offerBenefits.order),
          },
          tags: {
            orderBy: (offerTags, { asc }) => asc(offerTags.order),
          },
        },
      },
      testimonials: {
        orderBy: (testimonials, { asc }) => asc(testimonials.order),
      },
      details: true,
      results: true,
      certifications: true,
    },
  });
};

export const getCoachByLocationId = async (locationId: string) => {
  return (await getDBWithInit()).query.coaches.findFirst({
    where: (coaches, { eq }) => eq(coaches.ghlLocationId, locationId),
  });
};

export const getCoachByUsername = async (username: string) => {
  return (await getDBWithInit()).query.coaches.findFirst({
    where: (coaches, { eq }) => eq(coaches.username, username),
    columns: {
      // Exclude all sensitive/internal fields for public access
      ghlContactId: false,
      ghlLocationId: false,
      ghlUserId: false,
      stripeCustomerId: false,
      stripeAccountId: false,
      stripeSubscriptionId: false,
      stripeChargesEnabled: false,
      stripePayoutsEnabled: false,
      contractSigned: false,
      paymentTestApiKey: false,
      paymentTestPublishableKey: false,
      paymentLiveApiKey: false,
      paymentLivePublishableKey: false,
      email: false,
      phone: false,
      createdAt: false,
      updatedAt: false,
    },
    with: {
      profile: true,
      mainOffer: {
        with: {
          benefits: {
            orderBy: (offerBenefits, { asc }) => asc(offerBenefits.order),
          },
          components: {
            orderBy: (offerComponents, { asc }) => asc(offerComponents.order),
          },
          tags: {
            orderBy: (offerTags, { asc }) => asc(offerTags.order),
          },
        },
      },
      details: true,
      testimonials: {
        orderBy: (testimonials, { asc }) => asc(testimonials.order),
      },
      certifications: {
        orderBy: (certifications, { asc }) => asc(certifications.order),
      },
      results: {
        orderBy: (coachingResults, { asc }) => asc(coachingResults.order),
      },
      resources: {
        orderBy: (resources, { desc }) => desc(resources.publishedAt),
      },
    },
  });
};

export const getCoachByStripeAccountId = async (accountId: string) => {
  return (await getDBWithInit()).query.coaches.findFirst({
    where: (coaches, { eq }) => eq(coaches.stripeAccountId, accountId),
  });
};

export const getCoachByStripeCustomerId = async (customerId: string) => {
  return await (
    await getDBWithInit()
  ).query.coaches.findFirst({
    where: (coaches, { eq }) => eq(coaches.stripeCustomerId, customerId),
  });
};

export const getCoachByGhlContactId = async (contactId: string) => {
  return await (
    await getDBWithInit()
  ).query.coaches.findFirst({
    where: (coaches, { eq }) => eq(coaches.ghlContactId, contactId),
  });
};

export const getCoachByEmail = async (email: string) => {
  return await (
    await getDBWithInit()
  ).query.coaches.findFirst({
    where: (coaches, { eq }) => eq(coaches.email, email),
  });
};

export const getAllCoachEmails = async (): Promise<Set<string>> => {
  const result = await (
    await getDBWithInit()
  )
    .select({ email: coaches.email })
    .from(coaches)
    .where(sql`${coaches.email} IS NOT NULL AND ${coaches.email} != ''`);

  return new Set(result.map((row) => row.email.toLowerCase().trim()));
};

export const getCoachByReferralCode = async (referralCode: string) => {
  return (
    await (await getDBWithInit())
      .select()
      .from(coaches)
      .where(eq(coaches.referralCode, referralCode))
  )[0];
};

export const getReferralCount = async (coachId: string) => {
  const [result] = await (await getDBWithInit())
    .select({ count: count() })
    .from(coaches)
    .where(eq(coaches.referredByCoachId, coachId));
  return result.count;
};

export const getCoachProfile = async (id: string) => {
  return (await getDBWithInit()).query.profiles.findFirst({
    where: (profiles, { eq }) => eq(profiles.id, id),
  });
};

export const getCoachDetails = async (id: string) => {
  return (await getDBWithInit()).query.coachDetails.findFirst({
    where: (coachDetails, { eq }) => eq(coachDetails.id, id),
  });
};

export const createCoachProfile = async (profile: ProfileInsert) => {
  return (
    await (await getDBWithInit()).insert(profiles).values(profile).returning()
  )[0];
};

export const ensureCoachProfile = async (
  coachId: string,
  profileId?: string | null,
): Promise<ProfileSelect | undefined> => {
  // 1. Check if a profile already exists for this coachId
  const existingProfile = await (
    await getDBWithInit()
  ).query.profiles.findFirst({
    where: (profiles, { eq }) => eq(profiles.coachId, coachId),
  });

  if (existingProfile) {
    // If found, ensure the coach record points to it and return
    if (!profileId || profileId !== existingProfile.id) {
      await updateCoach(coachId, { profileId: existingProfile.id });
    }
    return existingProfile;
  }

  // 2. If no existing profile, proceed only if we intend to create one (original logic)
  if (!profileId) {
    const profile = await createCoachProfile({ coachId });
    await updateCoach(coachId, { profileId: profile.id });

    return profile;
  }

  // 3. If profileId was provided but didn't match an existing record (or something went wrong)
  // We might return undefined or fetch by the provided profileId explicitly if needed.
  // For now, return undefined if no profile was found or created.
  // Alternative: Fetch the profile using the provided profileId if it exists.
  if (profileId) {
    return getCoachProfile(profileId);
  }

  return undefined;
};

export const updateCoachProfile = async (
  id: string,
  profile: Partial<ProfileInsert>,
) => {
  return (
    await (await getDBWithInit())
      .update(profiles)
      .set(profile)
      .where(eq(profiles.id, id))
      .returning()
  )[0];
};

export const createCoachDetails = async (details: CoachDetailsInsert) => {
  return (
    await (await getDBWithInit())
      .insert(coachDetails)
      .values(details)
      .returning()
  )[0];
};

export const ensureCoachDetails = async (
  coachId: string,
  coachDetailsId?: string | null,
): Promise<CoachDetailsSelect | undefined> => {
  // 1. Check if details already exist for this coachId
  const existingDetails = await (
    await getDBWithInit()
  ).query.coachDetails.findFirst({
    where: (details, { eq }) => eq(details.coachId, coachId),
  });

  if (existingDetails) {
    // If found, ensure the coach record points to it and return
    if (!coachDetailsId || coachDetailsId !== existingDetails.id) {
      await updateCoach(coachId, { coachDetailsId: existingDetails.id });
    }
    return existingDetails;
  }

  // 2. If no existing details, create new ones (assuming coachId is sufficient to start)
  // Note: Unlike profile, details might require more initial data, but we create a minimal record for now.
  const newDetails = await createCoachDetails({ coachId });
  await updateCoach(coachId, { coachDetailsId: newDetails.id });

  return newDetails;
};

export const createOffer = async (offer: OfferInsert) => {
  return (
    await (await getDBWithInit()).insert(offers).values(offer).returning()
  )[0];
};

export const updateOffer = async (id: string, offer: Partial<OfferInsert>) => {
  return (
    await (await getDBWithInit())
      .update(offers)
      .set(offer)
      .where(eq(offers.id, id))
      .returning()
  )[0];
};

export const createTestimonial = async (testimonial: TestimonialInsert) => {
  return (
    await (await getDBWithInit())
      .insert(testimonials)
      .values(testimonial)
      .returning()
  )[0];
};

export const updateTestimonial = async (
  id: string,
  testimonial: Partial<TestimonialInsert>,
) => {
  return (
    await (await getDBWithInit())
      .update(testimonials)
      .set(testimonial)
      .where(eq(testimonials.id, id))
      .returning()
  )[0];
};

export const deleteTestimonial = async (id: string) => {
  return (
    await (await getDBWithInit())
      .delete(testimonials)
      .where(eq(testimonials.id, id))
      .returning()
  )[0];
};

export const createOfferBenefit = async (benefit: OfferBenefitInsert) => {
  return (
    await (await getDBWithInit())
      .insert(offerBenefits)
      .values(benefit)
      .returning()
  )[0];
};

export const updateOfferBenefit = async (
  id: string,
  benefit: Partial<OfferBenefitInsert>,
) => {
  return (
    await (await getDBWithInit())
      .update(offerBenefits)
      .set(benefit)
      .where(eq(offerBenefits.id, id))
      .returning()
  )[0];
};

export const deleteOfferBenefit = async (id: string) => {
  return (
    await (await getDBWithInit())
      .delete(offerBenefits)
      .where(eq(offerBenefits.id, id))
      .returning()
  )[0];
};

export const createOfferComponent = async (component: OfferComponentInsert) => {
  return (
    await (await getDBWithInit())
      .insert(offerComponents)
      .values(component)
      .returning()
  )[0];
};

export const updateOfferComponent = async (
  id: string,
  component: Partial<OfferComponentInsert>,
) => {
  return (
    await (await getDBWithInit())
      .update(offerComponents)
      .set(component)
      .where(eq(offerComponents.id, id))
      .returning()
  )[0];
};

export const deleteOfferComponent = async (id: string) => {
  return (
    await (await getDBWithInit())
      .delete(offerComponents)
      .where(eq(offerComponents.id, id))
      .returning()
  )[0];
};

export const getOfferComponents = async (offerId: string) => {
  return (await getDBWithInit()).query.offerComponents.findMany({
    where: (offerComponents, { eq }) => eq(offerComponents.offerId, offerId),
  });
};

export const createOfferTag = async (value: OfferTagInsert) => {
  return (
    await (await getDBWithInit()).insert(offerTags).values(value).returning()
  )[0];
};

export const updateOfferTag = async (
  id: string,
  value: Partial<OfferTagInsert>,
) => {
  return (
    await (await getDBWithInit())
      .update(offerTags)
      .set(value)
      .where(eq(offerTags.id, id))
      .returning()
  )[0];
};

export const deleteOfferTag = async (id: string) => {
  return (
    await (await getDBWithInit())
      .delete(offerTags)
      .where(eq(offerTags.id, id))
      .returning()
  )[0];
};

export const getOfferBenefits = async (offerId: string) => {
  return (await getDBWithInit()).query.offerBenefits.findMany({
    where: (offerBenefits, { eq }) => eq(offerBenefits.offerId, offerId),
  });
};

export const getOffer = async (id: string) => {
  return (await getDBWithInit()).query.offers.findFirst({
    where: (offers, { eq }) => eq(offers.id, id),
    with: {
      benefits: {
        orderBy: (offerBenefits, { asc }) => asc(offerBenefits.order),
      },
      components: {
        orderBy: (offerComponents, { asc }) => asc(offerComponents.order),
      },
      tags: {
        orderBy: (offerTags, { asc }) => asc(offerTags.order),
      },
      bonuses: {
        orderBy: (offerBonuses, { asc }) => asc(offerBonuses.order),
      },
      faqs: {
        orderBy: (offerFaqs, { asc }) => asc(offerFaqs.order),
      },
    },
  });
};

export const getOffersByCoachId = async (coachId: string) => {
  return (await getDBWithInit()).query.offers.findMany({
    where: (offers, { eq }) => eq(offers.coachId, coachId),
    with: {
      benefits: {
        orderBy: (offerBenefits, { asc }) => asc(offerBenefits.order),
      },
      components: {
        orderBy: (offerComponents, { asc }) => asc(offerComponents.order),
      },
    },
  });
};

export const getTestimonialsByCoachId = async (coachId: string) => {
  return (await getDBWithInit()).query.testimonials.findMany({
    where: (testimonials, { eq }) => eq(testimonials.coachId, coachId),
  });
};

export const createCoachingResult = async (value: CoachingResultsInsert) => {
  return (
    await (await getDBWithInit())
      .insert(coachingResults)
      .values(value)
      .returning()
  )[0];
};

export const updateCoachingResult = async (
  id: string,
  value: Partial<CoachingResultsInsert>,
) => {
  return (
    await (await getDBWithInit())
      .update(coachingResults)
      .set(value)
      .where(eq(coachingResults.id, id))
      .returning()
  )[0];
};

export const deleteCoachingResult = async (id: string) => {
  return (
    await (await getDBWithInit())
      .delete(coachingResults)
      .where(eq(coachingResults.id, id))
      .returning()
  )[0];
};

export const createCertification = async (value: CertificationsInsert) => {
  return (
    await (await getDBWithInit())
      .insert(certifications)
      .values(value)
      .returning()
  )[0];
};

export const updateCertification = async (
  id: string,
  value: Partial<CertificationsInsert>,
) => {
  return (
    await (await getDBWithInit())
      .update(certifications)
      .set(value)
      .where(eq(certifications.id, id))
      .returning()
  )[0];
};

export const deleteCertification = async (id: string) => {
  return (
    await (await getDBWithInit())
      .delete(certifications)
      .where(eq(certifications.id, id))
      .returning()
  )[0];
};

export const getCertificationsByCoachId = async (coachId: string) => {
  return (await getDBWithInit()).query.certifications.findMany({
    where: (certifications, { eq }) => eq(certifications.coachId, coachId),
  });
};

export async function updateCoachDetails(
  id: string,
  data: Partial<CoachDetailsSelect>,
) {
  return (
    await (await getDBWithInit())
      .update(coachDetails)
      .set(data)
      .where(eq(coachDetails.id, id))
      .returning()
  )[0];
}

export const createResource = async (newResource: ResourceInsert) => {
  return (
    await (await getDBWithInit())
      .insert(resources)
      .values(newResource)
      .returning()
  )[0];
};

export const updateResource = async (
  id: string,
  updatedResource: Partial<ResourceInsert>,
) => {
  return (
    await (await getDBWithInit())
      .update(resources)
      .set(updatedResource)
      .where(eq(resources.id, id))
      .returning()
  )[0];
};

export const deleteResource = async (id: string) => {
  await (await getDBWithInit()).delete(resources).where(eq(resources.id, id));
};

export const getResourcesByCoachId = async (coachId: string) => {
  return await (
    await getDBWithInit()
  ).query.resources.findMany({
    where: (resources, { eq }) => eq(resources.coachId, coachId),
  });
};

export const getResourceById = async (id: string) => {
  return (await getDBWithInit()).query.resources.findFirst({
    where: (resources, { eq }) => eq(resources.id, id),
  });
};

export const getLatestResourcesByCoachId = async (
  coachId: string,
  excludeResourceId: string,
) => {
  return await (
    await getDBWithInit()
  ).query.resources.findMany({
    where: (resources, { eq, and, ne }) =>
      and(eq(resources.coachId, coachId), ne(resources.id, excludeResourceId)),
    orderBy: (resources, { desc }) => desc(resources.publishedAt),
    limit: 3,
  });
};

export const createOfferBonus = async (bonus: OfferBonusInsert) => {
  return (
    await (await getDBWithInit()).insert(offerBonuses).values(bonus).returning()
  )[0];
};

export const updateOfferBonus = async (
  id: string,
  bonus: Partial<OfferBonusInsert>,
) => {
  return (
    await (await getDBWithInit())
      .update(offerBonuses)
      .set(bonus)
      .where(eq(offerBonuses.id, id))
      .returning()
  )[0];
};

export const deleteOfferBonus = async (id: string) => {
  return (
    await (await getDBWithInit())
      .delete(offerBonuses)
      .where(eq(offerBonuses.id, id))
      .returning()
  )[0];
};

export const createOfferFaq = async (faq: OfferFaqInsert) => {
  return (
    await (await getDBWithInit()).insert(offerFaqs).values(faq).returning()
  )[0];
};

export const updateOfferFaq = async (
  id: string,
  faq: Partial<OfferFaqInsert>,
) => {
  return (
    await (await getDBWithInit())
      .update(offerFaqs)
      .set(faq)
      .where(eq(offerFaqs.id, id))
      .returning()
  )[0];
};

export const deleteOfferFaq = async (id: string) => {
  return (
    await (await getDBWithInit())
      .delete(offerFaqs)
      .where(eq(offerFaqs.id, id))
      .returning()
  )[0];
};

export const deleteOffer = async (id: string) => {
  return (
    await (await getDBWithInit())
      .delete(offers)
      .where(eq(offers.id, id))
      .returning()
  )[0];
};
